import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { pods } from "@/lib/constants";
import { useStoreSynced } from "@/lib/store-synced";
import { cn } from "@/lib/utils";

interface OnboardingPageProps {
  className?: string;
}

const OnboardingPage = function OnboardingPage({
  className,
}: OnboardingPageProps) {
  const {
    setClientStatusChannel,
    clientUpdate,
    clientAssignPodNumber,
    clientSelf,
    clients,
  } = useStoreSynced(
    useShallow((s) => ({
      setClientStatusChannel: s.setClientStatusChannel,
      clientUpdate: s.clientUpdate,
      clientAssignPodNumber: s.clientAssignPodNumber,
      clientSelf: s.clientSelf,
      clients: s.clients,
    })),
  );

  useEffect(() => {
    setClientStatusChannel("OnboardingPage");
    return () => {
      setClientStatusChannel();
    };
  }, [setClientStatusChannel]);

  const isValidateEnabled = pods.some(
    (p) => p.pod === clientSelf?.podNumber && p.enabled,
  );

  const handlePodSelect = async (pod: string) => {
    if (pod === clientSelf?.podNumber) {
      await clientAssignPodNumber(undefined);
      return;
    }
    await clientAssignPodNumber(pod);
  };

  const handleValidate = async () => {
    await clientUpdate(true);
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center w-full h-full p-8 space-y-8",
        className,
      )}
    >
      <h1 className="text-2xl font-bold text-foreground">Onboarding</h1>

      <div className="w-full max-w-md space-y-6">
        <div>
          <label
            htmlFor="pod-selection"
            className="block text-sm font-medium mb-4 text-foreground"
          >
            Select POD Number
          </label>
          <div id="pod-selection" className="grid grid-cols-3 gap-2">
            {pods.map(({ pod, enabled }) => {
              const isSelected = clientSelf?.podNumber === pod;
              const isTaken = clients.some((m) => m.podNumber === pod);
              const isEnabled = enabled && !isTaken;
              return (
                <button
                  key={pod}
                  type="button"
                  disabled={!isEnabled}
                  onClick={() => handlePodSelect(pod)}
                  title={isTaken ? `Taken by someone else` : undefined}
                  className={cn(
                    "py-2 px-4 rounded text-lg font-medium transition-colors border-2",
                    "cursor-pointer",
                    isSelected && isEnabled
                      ? "bg-primary text-primary-foreground border-primary"
                      : isEnabled
                        ? "bg-muted border-muted-foreground hover:bg-muted/80"
                        : "cursor-not-allowed opacity-50 bg-muted border-muted",
                  )}
                >
                  {pod}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          data-testid="validate-button"
          onClick={handleValidate}
          disabled={!isValidateEnabled}
          className={cn(
            "w-full py-2 px-4 rounded font-medium transition-colors",
            isValidateEnabled
              ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
              : "bg-muted text-muted-foreground cursor-not-allowed",
          )}
        >
          VALIDATE
        </button>
      </div>
    </div>
  );
};

export { OnboardingPage };
